import { memo, useEffect, useMemo, useState } from "react";
import { Link, generatePath } from "react-router";

import { fetchMarkdown } from "@/lib/aws-amplify/storage";

import { PATHS } from "@/routes/paths";

import { FeatureIslandContainer, FeatureIslandHeader, FeatureIslandMain } from "@/components/features/_layouts";
import { QueryKeys } from "@/components/features/faq/model/repository/query";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";

import { MarkdownContent } from "./components";
import { extractToc } from "../utils/extract-toc";

type Props = {
	hotelID: string;
	fileName: string;
	type?: "host" | "guest";
};

const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string, text: string) => {
	e.preventDefault();

	setTimeout(() => {
		// まずIDで要素を探す
		let element = document.getElementById(id);

		// IDで見つからない場合、テキスト内容で見出し要素を探す
		if (!element) {
			const headings = document.querySelectorAll(
				".markdown-content h1, .markdown-content h2, .markdown-content h3, .markdown-content h4, .markdown-content h5, .markdown-content h6",
			);

			for (const heading of headings) {
				const headingText = heading.textContent?.trim() || "";
				if (headingText === text) {
					element = heading as HTMLElement;
					break;
				}
			}
		}

		if (!element) return;

		// 親要素からoverflow-autoのスクロールコンテナを探す
		const scrollContainer = element.closest(".overflow-auto");

		if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
			// スクロールコンテナ内でスクロール
			const containerRect = scrollContainer.getBoundingClientRect();
			const elementRect = element.getBoundingClientRect();
			const scrollTop = scrollContainer.scrollTop;
			const targetY = scrollTop + (elementRect.top - containerRect.top) - 20;

			scrollContainer.scrollTo({
				top: Math.max(0, targetY),
				behavior: "smooth",
			});
		} else {
			// windowでスクロール（フォールバック）
			const rect = element.getBoundingClientRect();
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const targetY = rect.top + scrollTop - 80;

			window.scrollTo({
				top: Math.max(0, targetY),
				behavior: "smooth",
			});
		}

		element.focus({ preventScroll: true });
	}, 0);
};

export const FAQDetail = memo(({ hotelID, fileName, type = "host" }: Props) => {
	// FAQ一覧ページへのパスを生成
	const faqListPath = useMemo(() => generatePath(PATHS.FAQ, { hotelID }), [hotelID]);

	// S3からマークダウンデータを取得
	const [markdown, setMarkdown] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(true);

	useEffect(() => {
		const fetchMarkdownData = async () => {
			try {
				setIsLoading(true);
				// type に応じて適切な QueryKey を選択し、fileName を組み合わせてS3からマークダウンファイルを取得
				// fileName に % などの特殊文字が含まれる場合があるためエンコード
				const basePath = type === "host" ? QueryKeys.hostMarkdown : QueryKeys.guestMarkdown;
				const s3Key = `${basePath}/${encodeURIComponent(fileName)}`;
				const markdownData = await fetchMarkdown(s3Key);
				setMarkdown(markdownData);
			} catch (error) {
				notifyUserEventInfo({
					variant: "error",
					title: "データの取得に失敗しました",
					description: "再度お試しください",
				});
				setMarkdown("");
			} finally {
				setIsLoading(false);
			}
		};

		fetchMarkdownData();
	}, [fileName, type]);

	// 目次を抽出
	const toc = useMemo(() => extractToc(markdown), [markdown]);

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader
				title="FAQ 詳細"
				rightChildren={
					<Button type="button" size={"lg"} variant="outline">
						<Link to={faqListPath} viewTransition>
							FAQ一覧に戻る
						</Link>
					</Button>
				}
			/>
			<FeatureIslandMain
				rightSidebar={
					toc.length > 0 ? (
						<aside className="sticky top-20 hidden h-fit w-64 shrink-0 laptop:top-24 laptop:block">
							<nav className="rounded-lg border border-zinc-100 bg-white p-4">
								<h2 className="mb-4 text-sm font-semibold text-zinc-900">目次</h2>
								<ul className="space-y-2">
									{toc.map((item) => (
										<li key={item.id} style={{ paddingLeft: `${(item.level - 1) * 1}rem` }}>
											<a
												href={`#${item.id}`}
												onClick={(e) => handleTocClick(e, item.id, item.text)}
												className="cursor-pointer text-sm text-zinc-600 transition-colors hover:text-zinc-900"
											>
												{item.text}
											</a>
										</li>
									))}
								</ul>
							</nav>
						</aside>
					) : undefined
				}
			>
				<div className="rounded-lg border border-zinc-100 bg-white p-8 shadow-sm">
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="text-zinc-600">読み込み中...</div>
						</div>
					) : (
						<MarkdownContent markdown={markdown} hotelID={hotelID} />
					)}
				</div>
			</FeatureIslandMain>
		</FeatureIslandContainer>
	);
});

FAQDetail.displayName = "FAQDetail";
